import type { BlockNode, LibraryBlock } from "../build";
import { ellipse, icon, logo, motion, photo, rect, text, WHITE } from "./helpers";

// ---------------------------------------------------------------------------
// Rahmen & Overlays: frames over the whole picture, gradients for readable
// text, device mockups, watermark
// ---------------------------------------------------------------------------

type Area = { width: number; height: number };

/** Four L-shaped corners `inset` from the edges (`length` long, `thickness` strong). */
function corners(
  { width, height }: Area,
  options: { inset: number; length: number; thickness: number; color: string; stagger?: number },
): BlockNode[] {
  const { inset: m, length: l, thickness: t, color } = options;
  const spots = [
    { x: m, y: m, dx: 0, dy: 0 },
    { x: width - m, y: m, dx: -1, dy: 0 },
    { x: m, y: height - m, dx: 0, dy: -1 },
    { x: width - m, y: height - m, dx: -1, dy: -1 },
  ];
  return spots.flatMap((spot, index) => {
    const move = motion("fade", { delay: index * (options.stagger ?? 0), duration: 400 });
    return [
      rect({
        x: spot.x + spot.dx * l,
        y: spot.y + spot.dy * t,
        w: l,
        h: t,
        fill: color,
        name: "Ecke",
        ...move,
      }),
      rect({
        x: spot.x + spot.dx * t,
        y: spot.y + spot.dy * l,
        w: t,
        h: l,
        fill: color,
        name: "Ecke",
        ...move,
      }),
    ];
  });
}

/** A white instant-photo border around the page with a caption in the wide bottom edge. */
function polaroidFrame({ width, height }: Area, withPhoto: boolean): BlockNode[] {
  const short = Math.min(width, height);
  const b = short * 0.05;
  const bottom = short * 0.2;
  const paper = "#fafaf9";
  const move = motion("fade", { duration: 500 });
  return [
    ...(withPhoto
      ? [
          photo({
            x: b,
            y: b,
            w: width - b * 2,
            h: height - b - bottom,
            radius: "none",
            name: "Bild",
            ...motion("zoom", { delay: 200 }),
          }),
        ]
      : []),
    rect({ x: 0, y: 0, w: width, h: b, fill: paper, name: "Rand oben", ...move }),
    rect({ x: 0, y: b, w: b, h: height - b, fill: paper, name: "Rand links", ...move }),
    rect({ x: width - b, y: b, w: b, h: height - b, fill: paper, name: "Rand rechts", ...move }),
    rect({
      x: b,
      y: height - bottom,
      w: width - b * 2,
      h: bottom,
      fill: paper,
      name: "Rand unten",
      ...move,
    }),
    text({
      text: "Sommer 2026",
      style: "quote",
      size: Math.min(72, bottom * 0.4),
      color: "#27272a",
      x: b * 2,
      y: height - bottom,
      w: width - b * 4,
      h: bottom,
      align: "center",
      ...motion("fade", { delay: 400 }),
    }),
  ];
}

export const FRAMES: LibraryBlock[] = [
  {
    id: "rahmen-kino",
    title: "Kino-Balken",
    category: "rahmen",
    tags: ["Letterbox", "Cinemascope", "Film", "schwarze Balken", "cinematic"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 6000,
    nodes: ({ width, height }) => {
      // Landscape: the bars of a 2.39:1 picture; upright formats get a hint of them.
      const bar =
        height > width
          ? height * 0.09
          : Math.min(height * 0.14, Math.max(height * 0.1, (height - width / 2.39) / 2));
      return [
        rect({
          x: 0,
          y: 0,
          w: width,
          h: bar,
          fill: "#000000",
          name: "Balken oben",
          ...motion("slide-down", { duration: 700 }),
        }),
        rect({
          x: 0,
          y: height - bar,
          w: width,
          h: bar,
          fill: "#000000",
          name: "Balken unten",
          ...motion("slide-up", { duration: 700 }),
        }),
      ];
    },
  },
  {
    id: "rahmen-rec",
    title: "REC-Kamera",
    category: "rahmen",
    tags: ["Aufnahme", "Kamera", "Camcorder", "Vlog", "Sucher", "Record"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 8000,
    only: "video",
    nodes: (area) => {
      const { width, height } = area;
      const short = Math.min(width, height);
      const m = short * 0.07;
      const top = height >= width * 1.2 ? height * 0.11 : m;
      const bottom = height >= width * 1.2 ? height * 0.2 : m;
      const inner = { width, height: height - top - bottom };
      const frame = corners(inner, {
        inset: m,
        length: short * 0.12,
        thickness: 6,
        color: "#ffffffe6",
      }).map((node) => ({ ...node, y: node.y + top }));
      const row = top + m + 30;
      return [
        ...frame,
        ellipse({
          x: m + 40,
          y: row + 12,
          w: 36,
          h: 36,
          fill: "#ef4444",
          name: "Aufnahme",
          ...motion("fade", { loop: "pulse" }),
        }),
        text({
          text: "REC",
          style: "heading-2",
          size: 40,
          color: WHITE,
          x: m + 92,
          y: row,
          w: 200,
          h: 60,
          ...motion("fade"),
        }),
        text({
          text: "00:00:12",
          style: "subline",
          size: 34,
          color: WHITE,
          x: width - m - 340,
          y: row,
          w: 300,
          h: 60,
          align: "right",
          ...motion("fade"),
        }),
        text({
          text: "4K · 60 fps",
          style: "caption",
          size: 28,
          color: WHITE,
          x: m + 40,
          y: height - bottom - m - 90,
          w: 300,
          h: 60,
          ...motion("fade"),
        }),
        icon({
          icon: "battery-full",
          x: width - m - 130,
          y: height - bottom - m - 100,
          w: 90,
          h: 80,
          color: WHITE,
          name: "Akku",
          ...motion("fade"),
        }),
      ];
    },
  },
  {
    id: "rahmen-ecken",
    title: "Ecken-Rahmen",
    category: "rahmen",
    tags: ["Ecken", "Rahmen", "Fokus", "Kontur"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 5000,
    nodes: (area) => {
      const short = Math.min(area.width, area.height);
      return corners(area, {
        inset: short * 0.06,
        length: short * 0.16,
        thickness: 12,
        color: "accent",
        stagger: 120,
      });
    },
  },
  {
    id: "rahmen-polaroid",
    title: "Polaroid-Rahmen",
    category: "rahmen",
    tags: ["Sofortbild", "Polaroid", "Rand", "Erinnerung", "Foto"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 5000,
    only: "video",
    nodes: (area) => polaroidFrame(area, false),
  },
  {
    id: "rahmen-polaroid-foto",
    title: "Polaroid mit Foto",
    category: "rahmen",
    tags: ["Sofortbild", "Polaroid", "Rand", "Erinnerung", "Foto"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    only: "design",
    nodes: (area) => polaroidFrame(area, true),
  },
  {
    id: "rahmen-verlauf-unten",
    title: "Verlauf unten",
    category: "rahmen",
    tags: ["Schatten", "Lesbarkeit", "dunkel", "Gradient", "Abdunkeln"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 6000,
    nodes: ({ width, height }) => [
      rect({
        x: 0,
        y: height * 0.45,
        w: width,
        h: height * 0.55,
        fill: { type: "linear", angle: 180, stops: ["#00000000", "#00000059", "#000000cc"] },
        name: "Verlauf",
        ...motion("fade", { duration: 600 }),
      }),
    ],
  },
  {
    id: "rahmen-verlauf-beide",
    title: "Verlauf oben & unten",
    category: "rahmen",
    tags: ["Schatten", "Lesbarkeit", "dunkel", "Gradient", "Abdunkeln", "Vignette"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 6000,
    nodes: ({ width, height }) => [
      rect({
        x: 0,
        y: 0,
        w: width,
        h: height * 0.32,
        fill: { type: "linear", angle: 180, stops: ["#000000b3", "#00000000"] },
        name: "Verlauf oben",
        ...motion("fade", { duration: 600 }),
      }),
      rect({
        x: 0,
        y: height * 0.6,
        w: width,
        h: height * 0.4,
        fill: { type: "linear", angle: 180, stops: ["#00000000", "#000000b3"] },
        name: "Verlauf unten",
        ...motion("fade", { duration: 600 }),
      }),
    ],
  },
  {
    id: "rahmen-farbrand",
    title: "Farbrand mit Logo",
    category: "rahmen",
    tags: ["Rand", "Marke", "Logo", "Branding", "Rahmen"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 6000,
    nodes: ({ width, height }) => {
      const t = Math.min(width, height) * 0.03;
      const move = motion("fade", { duration: 500 });
      return [
        rect({ x: 0, y: 0, w: width, h: t, fill: "primary", name: "Rand oben", ...move }),
        rect({ x: 0, y: t, w: t, h: height - t, fill: "primary", name: "Rand links", ...move }),
        rect({
          x: width - t,
          y: t,
          w: t,
          h: height - t,
          fill: "primary",
          name: "Rand rechts",
          ...move,
        }),
        rect({
          x: t,
          y: height - t,
          w: width - t * 2,
          h: t,
          fill: "primary",
          name: "Rand unten",
          ...move,
        }),
        rect({
          x: width / 2 - 220,
          y: height - t - 96,
          w: 440,
          h: 110,
          fill: "primary",
          radius: "md",
          name: "Lasche",
          ...motion("slide-up", { delay: 250 }),
        }),
        logo({
          x: width / 2 - 180,
          y: height - t - 86,
          w: 360,
          h: 76,
          on: "primary",
          align: "center",
          ...motion("slide-up", { delay: 300 }),
        }),
      ];
    },
  },
  {
    id: "rahmen-handy",
    title: "Handy-Rahmen",
    category: "rahmen",
    tags: ["Smartphone", "Mockup", "App", "Bildschirm", "iPhone"],
    width: 600,
    height: 1160,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 600,
        h: 1160,
        fill: "#111111",
        radius: 80,
        shadow: { color: "#00000059", y: 24, blur: 60 },
        name: "Gehäuse",
        ...motion("slide-up", { duration: 600 }),
      }),
      photo({
        x: 24,
        y: 24,
        w: 552,
        h: 1112,
        radius: 58,
        name: "Bildschirm",
        ...motion("slide-up", { duration: 600 }),
      }),
      rect({
        x: 220,
        y: 44,
        w: 160,
        h: 40,
        fill: "#111111",
        radius: "full",
        name: "Kamera",
        ...motion("slide-up", { duration: 600 }),
      }),
    ],
  },
  {
    id: "rahmen-browser",
    title: "Browser-Fenster",
    category: "rahmen",
    tags: ["Website", "Mockup", "Webseite", "Screenshot", "Fenster", "Agentur"],
    width: 1000,
    height: 700,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 1000,
        h: 700,
        fill: "#ffffff",
        radius: 24,
        shadow: { color: "#00000040", y: 20, blur: 50 },
        name: "Fenster",
        ...motion("pop"),
      }),
      ...["#ef4444", "#f59e0b", "#22c55e"].map((fill, index) =>
        ellipse({ x: 30 + index * 36, y: 28, w: 24, h: 24, fill, name: "Knopf", ...motion("pop") }),
      ),
      rect({
        x: 160,
        y: 20,
        w: 700,
        h: 40,
        fill: "#f4f4f5",
        radius: "full",
        name: "Adresse",
        ...motion("pop"),
      }),
      text({
        text: "deine-seite.de",
        style: "caption",
        size: 24,
        color: "#52525b",
        x: 180,
        y: 20,
        w: 660,
        h: 40,
        align: "center",
        ...motion("pop"),
      }),
      photo({
        x: 16,
        y: 80,
        w: 968,
        h: 604,
        radius: 12,
        name: "Seite",
        ...motion("fade", { delay: 250 }),
      }),
    ],
  },
  {
    id: "rahmen-wasserzeichen",
    title: "Logo-Wasserzeichen",
    category: "rahmen",
    tags: ["Logo", "Wasserzeichen", "Branding", "Marke", "Ecke"],
    width: 300,
    height: 110,
    anchor: "bottom-right",
    duration: 10000,
    nodes: [
      logo({
        x: 0,
        y: 0,
        w: 300,
        h: 110,
        on: "#000000",
        align: "right",
        opacity: 0.85,
        ...motion("fade", { duration: 600 }),
      }),
    ],
  },
];
